import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import Config from "../common/Config";
import { postGetUsersList } from "../../utils/usersApi";

function getInitialTrackUserId() {
  const till = localStorage.getItem("tillUserId");
  try {
    const ud = JSON.parse(
      localStorage.getItem("userData") || "{}"
    );
    const fb =
      ud?.id ?? ud?.user_id ?? ud?.userid ?? "";
    return String(till || fb || "").trim();
  } catch {
    return String(till || "").trim();
  }
}

function formatUserOptionLabel(u) {
  const login =
    (u?.username || u?.name || "").trim();
  const full = [u?.first_name, u?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const base =
    login ||
    full ||
    `User ${u?.id ?? ""}`;
  return full && base !== full
    ? `${base} (${full})`
    : base;
}

function extractLocationRows(json) {
  const d = json?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.locations)) return d.locations;
  if (Array.isArray(d?.records)) return d.records;
  if (Array.isArray(d?.rows)) return d.rows;
  if (Array.isArray(d?.list)) return d.list;
  if (Array.isArray(json?.locations)) return json.locations;
  if (Array.isArray(json?.result)) return json.result;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const firstArray = Object.values(d).find((v) => Array.isArray(v));
    if (firstArray) return firstArray;
  }
  return [];
}

function parseCoordPair(item) {
  if (!item || typeof item !== "object") return null;

  const nested =
    item.location ||
    item.coords ||
    item.coordinate ||
    item.position ||
    item.gps ||
    {};

  const sources = [item, nested];

  const tryLat = (o) =>
    o?.latitude ??
    o?.Latitude ??
    o?.lat ??
    o?.Lat ??
    o?.device_latitude ??
    o?.gps_lat ??
    o?.y;

  const tryLng = (o) =>
    o?.longitude ??
    o?.Longitude ??
    o?.lng ??
    o?.long ??
    o?.Lng ??
    o?.device_longitude ??
    o?.gps_lng ??
    o?.gps_long ??
    o?.x;

  let lat;
  let lng;

  for (const o of sources) {
    if (!o || typeof o !== "object") continue;
    const rl = tryLat(o);
    const rj = tryLng(o);
    if (rl != null && rl !== "" && rj != null && rj !== "") {
      lat =
        typeof rl === "string"
          ? Number.parseFloat(String(rl).replace(/,/g, ""))
          : Number(rl);
      lng =
        typeof rj === "string"
          ? Number.parseFloat(String(rj).replace(/,/g, ""))
          : Number(rj);
      break;
    }
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    if (
      Array.isArray(item.coordinates) &&
      item.coordinates.length >= 2
    ) {
      lng = Number(item.coordinates[0]);
      lat = Number(item.coordinates[1]);
    }
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    if (
      Array.isArray(item.latlng) &&
      item.latlng.length >= 2
    ) {
      lat = Number(item.latlng[0]);
      lng = Number(item.latlng[1]);
    }
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    const t = lat;
    lat = lng;
    lng = t;
  }

  if (
    lat > 45 &&
    lng > 8 &&
    lng < 45 &&
    lat > lng
  ) {
    const t = lat;
    lat = lng;
    lng = t;
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  return { lat, lng };
}

function parseBatteryPct(item) {
  if (!item || typeof item !== "object") return null;
  const v =
    item.battery_percentage ??
    item.batteryPercentage ??
    item.battery_pct ??
    item.battery;
  if (v == null || v === "") return null;
  const n = Number.parseFloat(
    String(v).replace(/,/g, "")
  );
  if (!Number.isFinite(n)) return null;
  return Math.round(
    Math.min(100, Math.max(0, n))
  );
}

function haversineKm(a, b) {
  const R = 6371;
  const dLat =
    ((b.lat - a.lat) * Math.PI) / 180;
  const dLng =
    ((b.lng - a.lng) * Math.PI) / 180;
  const la = (a.lat * Math.PI) / 180;
  const lb = (b.lat * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la) *
      Math.cos(lb) *
      Math.sin(dLng / 2) ** 2;
  return (
    2 * R * Math.asin(Math.min(1, Math.sqrt(x)))
  );
}

function sumTrackKm(pts) {
  if (pts.length < 2) return 0;
  let s = 0;
  for (let i = 1; i < pts.length; i++) {
    s += haversineKm(pts[i - 1], pts[i]);
  }
  return s;
}

function formatMarkerPopup(p, label) {
  const when = [p.trip_date, p.trip_time]
    .filter(Boolean)
    .join(" ");
  const bat =
    p.batteryPct != null
      ? `Battery ${p.batteryPct}%`
      : "";
  const parts = [label, when, bat].filter(Boolean);
  return parts.join(" · ");
}

const MAPBOX_DIRECTIONS_TOKEN =
  (typeof process !== "undefined" &&
    process.env.REACT_APP_MAPBOX_ACCESS_TOKEN) ||
  "pk.eyJ1IjoiZGRzbmFja3MiLCJhIjoiY21jbHFtbmNiMDk2dTJxczlpMnIxbWNrcCJ9.QEX0ufFo7NoOSeN6EzhLXw";

function mergeLineCoordinates(segments) {
  const out = [];
  for (const seg of segments) {
    if (!seg?.length) continue;
    if (out.length === 0) {
      out.push(...seg);
      continue;
    }
    const last = out[out.length - 1];
    const first = seg[0];
    if (
      Math.abs(last[0] - first[0]) < 1e-7 &&
      Math.abs(last[1] - first[1]) < 1e-7
    ) {
      out.push(...seg.slice(1));
    } else {
      out.push(...seg);
    }
  }
  return out;
}

async function fetchMapboxDrivingSegments(points) {
  const pt = points.map((p) => [p.lng, p.lat]);
  if (pt.length < 2) return null;

  const MAPBOX_MAX = 25;
  const segments = [];
  let start = 0;

  while (start < pt.length - 1) {
    const end = Math.min(start + MAPBOX_MAX - 1, pt.length - 1);
    const chunk = pt.slice(start, end + 1);
    const coordString = chunk.map(([lng, lat]) => `${lng},${lat}`).join(";");
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordString}?geometries=geojson&overview=full&access_token=${encodeURIComponent(
      MAPBOX_DIRECTIONS_TOKEN
    )}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const coords = data?.routes?.[0]?.geometry?.coordinates;
    if (!coords?.length) return null;
    segments.push(coords);
    start = end;
  }

  return mergeLineCoordinates(segments);
}

async function fetchOsrmDrivingSegments(points) {
  const pt = points.map((p) => [p.lng, p.lat]);
  if (pt.length < 2) return null;

  const OSRM_MAX = 90;
  const segments = [];
  let start = 0;

  while (start < pt.length - 1) {
    const end = Math.min(start + OSRM_MAX - 1, pt.length - 1);
    const chunk = pt.slice(start, end + 1);
    const coordString = chunk.map(([lng, lat]) => `${lng},${lat}`).join(";");
    const url = `https://router.project-osrm.org/route/v1/driving/${coordString}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.code !== "Ok") return null;
    const coords = data?.routes?.[0]?.geometry?.coordinates;
    if (!coords?.length) return null;
    segments.push(coords);
    start = end;
  }

  return mergeLineCoordinates(segments);
}

function alignRouteLineToMarkerEndpoints(
  coords,
  points
) {
  if (
    !coords?.length ||
    coords.length < 2 ||
    points.length < 2
  ) {
    return coords;
  }
  const first = [
    points[0].lng,
    points[0].lat,
  ];
  const last = [
    points[points.length - 1].lng,
    points[points.length - 1].lat,
  ];
  const out = coords.slice();
  out[0] = first;
  out[out.length - 1] = last;
  return out;
}

async function fetchRoadLineCoordinates(points) {
  if (points.length < 2) return null;

  const straight = points.map((p) => [p.lng, p.lat]);

  try {
    const mb = await fetchMapboxDrivingSegments(points);
    if (mb?.length >= 2) return mb;
  } catch {
    void 0;
  }

  try {
    const osrm = await fetchOsrmDrivingSegments(points);
    if (osrm?.length >= 2) return osrm;
  } catch {
    void 0;
  }

  return straight;
}

const LocationTrackReport = () => {
  const routeSourceId = "location-track-route-source";
  const routeLayerId = "location-track-route-layer";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [selectedUserId, setSelectedUserId] =
    useState(getInitialTrackUserId);
  const [userOptions, setUserOptions] =
    useState([]);
  const [usersLoading, setUsersLoading] =
    useState(false);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const resizeObserverRef = useRef(null);
  const hasFitBoundsRef = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  // =========================
  // FETCH API
  // =========================
  const fetchLocations = useCallback(async () => {
    const uid = String(selectedUserId || "").trim();
    if (!uid) {
      setRows([]);
      setError("");
      setInfo("Select a user to view location track");
      hasFitBoundsRef.current = false;
      return;
    }

    try {
      setLoading(true);
      setError("");
      setInfo("");

      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken");

      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      const query = new URLSearchParams();
      query.set("user_id", uid);

      const url = `${Config.API_URL}/ws-get-device-location?${query.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const json = await response.json();

      const list = extractLocationRows(json);

      if (!list.length) {
        setRows([]);
        setInfo(json?.message || "No location found");
        hasFitBoundsRef.current = false;
        return;
      }

      hasFitBoundsRef.current = false;
      setRows(list);
      setInfo("");
    } catch (err) {
      setError(err?.message || "Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    let cancelled = false;
    async function loadUsers() {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken");
      if (!token) return;
      setUsersLoading(true);
      try {
        const json = await postGetUsersList(
          `${Config.API_URL}/get_users_list`,
          token,
          {
            draw: 1,
            start: 0,
            length: 500,
            searchValue: "",
          }
        );
        const list =
          json?.data && Array.isArray(json.data)
            ? json.data
            : [];
        if (!cancelled) setUserOptions(list);
      } catch {
        if (!cancelled) setUserOptions([]);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    }
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  // =========================
  // INITIAL API CALL
  // =========================
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // =========================
  // PREPARE POINTS
  // =========================
  const selectedUserMissing =
    Boolean(
      String(selectedUserId || "").trim()
    ) &&
    !userOptions.some(
      (u) =>
        String(u?.id) === String(selectedUserId)
    );

  const points = useMemo(() => {
    const parsed = rows
      .map((item) => {
        const pair = parseCoordPair(item);
        if (!pair) return null;

        return {
          lat: pair.lat,
          lng: pair.lng,
          trip_date:
            item?.trip_date ??
            item?.tripDate ??
            item?.date,
          trip_time:
            item?.trip_time ??
            item?.tripTime ??
            item?.time,
          batteryPct: parseBatteryPct(item),
        };
      })
      .filter(Boolean);

    parsed.sort((a, b) => {
      const sa = `${a.trip_date ?? ""} ${a.trip_time ?? ""}`.trim();
      const sb = `${b.trip_date ?? ""} ${b.trip_time ?? ""}`.trim();
      if (sa && sb) return sa.localeCompare(sb);
      return 0;
    });

    return parsed;
  }, [rows]);

  const trackStats = useMemo(() => {
    if (!points.length) return null;
    const km = sumTrackKm(points);
    const startBat = points[0].batteryPct;
    const endBat =
      points[points.length - 1].batteryPct;
    return { km, startBat, endBat };
  }, [points]);

  // =========================
  // MAP INITIALIZATION
  // =========================
  useEffect(() => {
    if (!mapContainerRef.current) return;

    // prevent recreate map
    if (mapRef.current) return;

    mapRef.current = new maplibregl.Map({
      container: mapContainerRef.current,

      style: {
        version: 8,

        sources: {
          osm: {
            type: "raster",
            tiles: [
              "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
            ],
            tileSize: 256,
            attribution: "© OpenStreetMap contributors",
            maxzoom: 19,
          },
        },

        layers: [
          {
            id: "osm",
            type: "raster",
            source: "osm",
            minzoom: 0,
            maxzoom: 22,
          },
        ],
      },

      center: [73.8567, 18.5204],
      zoom: 11,
    });

    mapRef.current.addControl(
      new maplibregl.NavigationControl(),
      "top-right"
    );

    mapRef.current.once("load", () => {
      setMapLoaded(true);
      mapRef.current?.resize();
    });

    // resize observer
    if (typeof ResizeObserver !== "undefined") {
      resizeObserverRef.current =
        new ResizeObserver(() => {
          mapRef.current?.resize();
        });

      resizeObserverRef.current.observe(
        mapContainerRef.current
      );
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      markersRef.current.forEach((marker) =>
        marker.remove()
      );

      markersRef.current = [];

      mapRef.current?.remove();
      mapRef.current = null;
      setMapLoaded(false);
    };
  }, []);

  // =========================
  // UPDATE MARKERS & ROUTE (road-snapped line)
  // =========================
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    const map = mapRef.current;

    let cancelled = false;

    async function applyOverlay() {
      if (map.getLayer(routeLayerId)) {
        map.removeLayer(routeLayerId);
      }
      if (map.getSource(routeSourceId)) {
        map.removeSource(routeSourceId);
      }

      markersRef.current.forEach((marker) =>
        marker.remove()
      );

      markersRef.current = [];

      if (!points.length) return;

      let lineCoordinates =
        points.length >= 2 ? await fetchRoadLineCoordinates(points) : null;

      if (cancelled) return;

      if (
        !lineCoordinates?.length ||
        lineCoordinates.length < 2
      ) {
        lineCoordinates = points.map((point) => [
          point.lng,
          point.lat,
        ]);
      }

      lineCoordinates =
        alignRouteLineToMarkerEndpoints(
          lineCoordinates,
          points
        );

      if (lineCoordinates.length >= 2) {
        try {
          map.addSource(routeSourceId, {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: lineCoordinates,
              },
              properties: {},
            },
          });
          map.addLayer({
            id: routeLayerId,
            type: "line",
            source: routeSourceId,
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#1976d2",
              "line-width": 6,
              "line-opacity": 1,
            },
          });
        } catch {
          try {
            if (map.getLayer(routeLayerId)) map.removeLayer(routeLayerId);
            if (map.getSource(routeSourceId)) map.removeSource(routeSourceId);
          } catch {
            void 0;
          }
        }
      }

      if (cancelled) return;

      const bounds = new maplibregl.LngLatBounds();

      points.forEach((point) => {
        bounds.extend([point.lng, point.lat]);
      });

      lineCoordinates.forEach(([lng, lat]) => {
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          bounds.extend([lng, lat]);
        }
      });

      if (!hasFitBoundsRef.current && points.length) {
        map.fitBounds(bounds, {
          padding: 40,
          maxZoom: 16,
        });

        hasFitBoundsRef.current = true;
      }

      const startMarker =
        new maplibregl.Marker({
          color: "#2e7d32",
        })
          .setLngLat([
            points[0].lng,
            points[0].lat,
          ])
          .setPopup(
            new maplibregl.Popup().setText(
              formatMarkerPopup(
                points[0],
                "Start"
              )
            )
          )
          .addTo(map);

      markersRef.current.push(startMarker);

      if (points.length > 1) {
        const endMarker =
          new maplibregl.Marker({
            color: "#d32f2f",
          })
            .setLngLat([
              points[points.length - 1].lng,
              points[points.length - 1].lat,
            ])
            .setPopup(
              new maplibregl.Popup().setText(
                formatMarkerPopup(
                  points[points.length - 1],
                  "End"
                )
              )
            )
            .addTo(map);

        markersRef.current.push(endMarker);
      }
    }

    applyOverlay();

    return () => {
      cancelled = true;
    };
  }, [points, mapLoaded]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">
          Location Track Report
        </Typography>

        <Button
          variant="contained"
          onClick={fetchLocations}
          disabled={
            loading ||
            !String(selectedUserId || "").trim()
          }
        >
          Refresh
        </Button>

        <FormControl
          size="small"
          sx={{ minWidth: 280 }}
          className="input-border text-field"
        >
          <InputLabel id="location-track-user-label">
            User
          </InputLabel>
          <Select
            labelId="location-track-user-label"
            label="User"
            value={
              String(selectedUserId || "") === ""
                ? ""
                : String(selectedUserId)
            }
            onChange={(e) => {
              const v = String(e.target.value);
              setSelectedUserId(v);
              hasFitBoundsRef.current = false;
            }}
            disabled={usersLoading}
          >
            <MenuItem value="">
              <em>Select user</em>
            </MenuItem>
            {selectedUserMissing && (
              <MenuItem
                value={String(selectedUserId)}
              >
                User id {selectedUserId}
              </MenuItem>
            )}
            {userOptions.map((u) => (
              <MenuItem
                key={u.id}
                value={String(u.id)}
              >
                {formatUserOptionLabel(u)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {trackStats &&
        String(selectedUserId || "").trim() &&
        !error &&
        points.length > 0 && (
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              Distance:{" "}
              {trackStats.km.toFixed(2)} km
            </Typography>
            <Typography variant="body2">
              Battery:{" "}
              {trackStats.startBat != null &&
              trackStats.endBat != null &&
              trackStats.startBat !==
                trackStats.endBat
                ? `start ${trackStats.startBat}% · end ${trackStats.endBat}%`
                : trackStats.endBat != null
                  ? `${trackStats.endBat}%`
                  : trackStats.startBat != null
                    ? `${trackStats.startBat}%`
                    : "—"}
            </Typography>
          </Stack>
        )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!error && info && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}

      <Paper variant="outlined">
        <Box
          ref={mapContainerRef}
          sx={{
            width: "100%",
            height: 420,

            "& .maplibregl-map": {
              width: "100%",
              height: "100%",
            },

            "& .mapboxgl-map": {
              width: "100%",
              height: "100%",
            },

            "& .maplibregl-canvas": {
              width: "100% !important",
              height: "100% !important",
            },

            "& .mapboxgl-canvas": {
              width: "100% !important",
              height: "100% !important",
            },

            "& img": {
              maxWidth: "none !important",
            },
          }}
        />
      </Paper>

      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 3,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default LocationTrackReport;